import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { TextField, PasswordField } from '@/components/ui/TextField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useLogin, useResendVerification } from '@/auth/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { ApiError } from '@/lib/api';
import { ROUTES } from '@/lib/config';
import { useT } from '@/i18n/useT';
import { rules } from './rules';

interface Form {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useT();
  const login = useLogin();
  const resend = useResendVerification();
  const location = useLocation();
  const nav = location.state as { justSignedUp?: boolean; email?: string } | null;
  const [serverError, setServerError] = useState('');
  // Set when a correct login is refused because the email isn't verified — turns
  // on the "resend verification email" affordance.
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Form>({ defaultValues: { email: nav?.email ?? '' } });

  const onSubmit = handleSubmit(async (data) => {
    setServerError('');
    setUnverified(false);
    setResent(false);
    try {
      await login.mutateAsync(data);
      // Routing after sign-in is handled by RequireGuest (which now re-renders as
      // authed): a customer WITHOUT an active subscription is sent to the pricing
      // page; everyone else to the trading dashboard at /. Centralizing it there
      // avoids this handler racing the guard's own redirect.
    } catch (e) {
      if (e instanceof ApiError && e.code === 'email_not_verified') setUnverified(true);
      setServerError(errorMessage(e));
    }
  });

  const onResend = async () => {
    setResent(false);
    try {
      await resend.mutateAsync({ email: getValues('email') });
    } finally {
      // Non-enumerating endpoint: always the same outcome, so always confirm.
      setResent(true);
    }
  };

  return (
    <Card title={t('login.title')} subtitle={t('login.subtitle')}>
      {nav?.justSignedUp && !resent && <Alert kind="success">{t('login.checkEmail')}</Alert>}
      {resent && <Alert kind="success">{t('login.verificationResent')}</Alert>}
      <Alert kind="error">{serverError}</Alert>
      {unverified && !resent && (
        <p className="foot">
          <button type="button" className="link" onClick={onResend} disabled={resend.isPending}>
            {resend.isPending ? t('login.resending') : t('login.resendVerification')}
          </button>
        </p>
      )}
      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('common.email')}
          type="email"
          autoComplete="email"
          autoFocus
          error={errors.email?.message}
          disabled={login.isPending}
          {...register('email', rules.email)}
        />
        <PasswordField
          label={t('common.password')}
          autoComplete="current-password"
          error={errors.password?.message}
          disabled={login.isPending}
          {...register('password', rules.password)}
        />
        <Button type="submit" busy={login.isPending}>
          {login.isPending ? t('login.working') : t('login.cta')}
        </Button>
      </form>
      <p className="foot">
        <Link className="link" to={ROUTES.forgot}>
          {t('login.forgot')}
        </Link>
      </p>
      <p className="foot">
        {t('login.noAccount')}{' '}
        <Link className="link" to={ROUTES.signup}>
          {t('login.signUp')}
        </Link>
      </p>
    </Card>
  );
}
